import React, {
  memo, useCallback, useContext, useEffect, useId, useRef, useState,
} from 'react'

import {
  Button, ChevronDown, ChevronUp, Icon, Spinner,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import CommentThread from 'Pods/ThreeDStudio/Shared/Comments/Components/CommentThread/CommentThread'
import Composer from 'Pods/ThreeDStudio/Shared/Comments/Components/Composer/Composer'
import useModelComments from 'Pods/ThreeDStudio/Shared/Comments/Hooks/useModelComments'
import { openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'

// The image's comment thread under its prompt. Anyone can read it; only a signed-in viewer
// gets the composer and the Reply controls, while a signed-out one is offered to log in in
// its place. Neither shows until /load-services.js resolves the auth state, which reads as
// signed out until then. The count includes replies, straight from the comments request,
// and follows every add and delete.
function Comments({ slug }) {
  const t = useTranslation()
  const { locale, userLoggedIn, pageConfigLoaded } = useContext(ThreeDStudioPageContext)
  const pageConfig = usePageConfig()
  const [isExpanded, setIsExpanded] = useState(true)
  const sectionRef = useRef(null)
  const sentinelRef = useRef(null)
  const bodyId = useId()

  // syncOpenedModel off: this count is an image run's and must not land on the opened 3D
  // model's footer badge.
  const {
    comments,
    totalCommentsCount,
    hasMore,
    loadMore,
    isLoading,
    loadFailed,
    submitFailed,
    limitReached,
    isSubmitting,
    addComment,
    addReply,
    deleteComment,
  } = useModelComments({ slug, isOpen: true, syncOpenedModel: false })

  const commentsCount = totalCommentsCount ?? 0
  const canComment = !!userLoggedIn

  // Infinite scroll over the paginated root comments. No explicit root: the observer already
  // accounts for the details panel clipping the sentinel while it scrolls.

  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore()
        }
      },
      { rootMargin: '200px', threshold: 0 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, loadMore, isExpanded])

  const handleLogin = useCallback(() => {
    openSignInModal('login')
  }, [])

  const handleToggle = useCallback(() => {
    setIsExpanded(wasExpanded => !wasExpanded)
  }, [])

  // The Delete button unmounts with its comment and would drop focus to <body>, outside the
  // dialog, where Escape no longer closes it. Deferred a frame so the confirm modal's own
  // focus restore runs first.
  const handleDelete = useCallback((commentId) => {
    deleteComment(commentId)
    requestAnimationFrame(() => sectionRef.current?.focus())
  }, [deleteComment])

  return (
    <section
      ref={sectionRef}
      className="image-gallery-modal__comments"
      aria-label={t('commentsModal.ariaLabel')}
      tabIndex={-1}
    >
      <button
        type="button"
        className="image-gallery-modal__comments-toggle"
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        onClick={handleToggle}
      >
        <span className="image-gallery-modal__comments-title cgt-heading-50">
          {t('commentsModal.commentsCount', { count: commentsCount })}
        </span>
        <Icon svgComponent={isExpanded ? ChevronUp : ChevronDown} size={iconSize} color="dark" notSpaced />
      </button>
      {isExpanded && (
        <div id={bodyId} className="image-gallery-modal__comments-body">
          {pageConfigLoaded && canComment && (
            <Composer
              placeholder={t('commentsModal.placeholder')}
              username={pageConfig?.currentUserUsername || ''}
              avatarUrl={pageConfig?.currentUserAvatarUrl || null}
              isSubmitting={isSubmitting}
              onSubmit={addComment}
            />
          )}
          {pageConfigLoaded && !canComment && (
            <div className="comments-modal__login-prompt">
              <span className="cgt-body-base">{t('commentsModal.loginPrompt')}</span>
              <Button
                type="secondary"
                size="small"
                actionType="button"
                customClass="image-gallery-modal__login-button"
                onClick={handleLogin}
              >
                {t('commentsModal.login')}
              </Button>
            </div>
          )}
          {(submitFailed || limitReached) && (
            <div className="comments-modal__error" role="alert">
              {t(limitReached ? 'commentsModal.limitReached' : 'commentsModal.submitError')}
            </div>
          )}
          {comments.length === 0 && <EmptyState isLoading={isLoading} loadFailed={loadFailed} />}
          {comments.length > 0 && (
            <ul className="comments-modal__list">
              {comments.map(comment => (
                <CommentThread
                  key={comment.id}
                  comment={comment}
                  currentUserId={pageConfig?.currentUserId ?? null}
                  locale={locale}
                  canComment={canComment}
                  isSubmitting={isSubmitting}
                  creatorLabel={t('imageComments.creatorBadge')}
                  onReply={addReply}
                  onDelete={handleDelete}
                />
              ))}
            </ul>
          )}
          {comments.length > 0 && isLoading && hasMore && (
            <div className="comments-modal__state" aria-live="polite">
              <Spinner size="small" />
            </div>
          )}
          {comments.length > 0 && loadFailed && (
            <div className="comments-modal__error" role="alert">
              {t('commentsModal.loadError')}
            </div>
          )}
          {comments.length > 0 && hasMore && (
            <div className="comments-modal__sentinel" ref={sentinelRef} aria-hidden="true" />
          )}
        </div>
      )}
    </section>
  )
}

Comments.propTypes = {
  slug: string.isRequired,
}

export default memo(Comments)
